// src/app/modules/auth/services/auth-http/auth-http.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { AuthModel } from '../../models/auth.model';
import { UserModel } from '../../models/user.model';
import { environment } from 'src/environments/environment';
import { CustomerModel } from '../../models/customer.model';
import { EntityModel } from '../../models/entity.model';
import { PortalSearchDebounceService } from 'src/app/portal-ui/shared/ui/directives/portal-search-debounce.service';

/** Resolved at call time so white-label apiUrl rewrite in main.ts is visible. */
function authApiUrl(): string {
  return `${environment.apiUrl}/auth`;
}

// Managed tenants structure as returned by /auth/managed-customers
export interface ManagedTenantDto {
  tenant_id: number;
  tenant_name: string;
  total_customers?: number;
  customers: CustomerModel[];
  has_more_customers?: boolean;
}

// Tenant-specific page response when requesting with tenant_id
export interface TenantCustomersPageDto {
  total: number;
  customers: CustomerModel[];
  has_more: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthHTTPService {
  constructor(
    private http: HttpClient,
    private searchDebounce: PortalSearchDebounceService,
  ) { }

  sessionFromCookie(): Observable<string | undefined> {
    return this.http.get<{ success?: boolean; data?: { token?: string } }>(`${authApiUrl()}/session`).pipe(
      map((resp) => resp?.data?.token || undefined),
      catchError(() => of(undefined)),
    );
  }

  /** Apply portal_session HttpOnly cookie from the current Bearer JWT (same-origin). */
  syncSessionCookie(token: string): Observable<boolean> {
    if (!token) {
      return of(false);
    }
    return this.http.post<{ success?: boolean }>(
      `${authApiUrl()}/session/sync`,
      {},
      {
        headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
        withCredentials: true,
      },
    ).pipe(
      map((resp) => resp?.success === true),
      catchError(() => of(false)),
    );
  }

  clearSessionCookie(): Promise<void> {
    try {
      return fetch(`${authApiUrl()}/logout`, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
      }).then(
        () => undefined,
        () => undefined,
      );
    } catch {
      // Best-effort: local state is still cleared by AuthService.logout().
      return Promise.resolve();
    }
  }

  /**
   * 1) login => POST /auth/login => { success, token, user, mfa_enroll_advised }
   *    returns AuthModel | undefined
   */
  login(email: string, password: string): Observable<AuthModel | undefined> {
    if (!email || !password) {
      console.warn('[AuthHTTP] login called without email/password');
      return of(undefined);
    }

    console.log(`[AuthHTTP] attempting login for`, email);
    return this.http
      .post<any>(`${authApiUrl()}/login`, { email, password })
      .pipe(
        tap(resp => console.log('[AuthHTTP] raw login response', resp)),
        map(resp => {
          const data = resp?.data;
          const errorCode = resp?.error_code || resp?.code;

          // Login OK but user must pick a tenant/customer context.
          // MFA challenge / mandatory enrollment (email login)
          if (
            resp?.success &&
            data?.token &&
            (data?.mfa_required === true || data?.mfa_enroll_required === true || data?.mfa_pending === true)
          ) {
            const auth = new AuthModel();
            auth.authToken = data.token;
            auth.refreshToken = '';
            auth.expiresIn = new Date(Date.now() + 24 * 60 * 60 * 1000);
            auth.userId = data.user?.id ?? 0;
            auth.customerId = null;
            auth.mfa_required = data.mfa_required === true || data.mfa_enroll_required === true;
            auth.mfa_enroll_required = data.mfa_enroll_required === true;
            auth.mfa_pending = true;
            auth.mfa_enroll_advised = data.mfa_enroll_required === true;
            if (data.user?.id) {
              auth.user = {
                id: data.user.id,
                email: data.user.email,
                username: data.user.username,
                language: data.user.language,
              };
            }
            console.log('[AuthHTTP] mapped AuthModel (MFA challenge):', auth);
            return auth;
          }

          if (
            !resp?.success &&
            errorCode === 'AUTH_MULTIPLE_CONTEXTS_AVAILABLE' &&
            data?.token
          ) {
            const auth = new AuthModel();
            auth.authToken = data.token;
            auth.refreshToken = data.refresh_token || '';
            auth.expiresIn = new Date(Date.now() + 24 * 60 * 60 * 1000);
            auth.userId = data.user?.id ?? 0;
            auth.customerId = null;
            auth.tenant_id = undefined;
            auth.customer_id = undefined;
            auth.context_pending = true;
            auth.available_contexts = Array.isArray(data.available_contexts)
              ? data.available_contexts
              : [];
            auth.mfa_enroll_advised = data.mfa_enroll_advised;
            auth.mfa_verify_required = data.mfa_status?.mfa_verify_required ?? data.mfa_verify_required;
            auth.mfa_last = data.mfa_status?.mfa_last ?? data.mfa_last;
            if (data.user?.id) {
              auth.user = {
                id: data.user.id,
                email: data.user.email,
                username: data.user.username,
                language: data.user.language,
              };
            }
            console.log('[AuthHTTP] mapped AuthModel (multiple contexts):', auth);
            return auth;
          }

          if (!resp?.success) {
            console.warn('[AuthHTTP] login.success=false', resp);
            // Throw an error with the message from the API response
            throw new Error(resp.message || 'Login failed');
          }
          
          // API response structure: { success, data: { user, token, tenant_id, customer_id, mfa_enroll_advised } }
          if (!data?.token || !data?.user) {
            console.error('[AuthHTTP] missing token or user in response data', resp);
            throw new Error('Invalid response format: missing token or user data');
          }

          const auth = new AuthModel();
          auth.authToken = data.token;
          // Some backends return refresh_token in new format
          auth.refreshToken = data.refresh_token || '';
          // No expiresIn in new response, so set to 1 day by default (or adjust as needed)
          auth.expiresIn = new Date(Date.now() + 24 * 60 * 60 * 1000);
          auth.userId = data.user.id;
          // Use customer_id from response
          auth.customerId = data.customer_id || null;
          // Map the new fields from the API response
          auth.tenant_id = data.tenant_id;
          auth.customer_id = data.customer_id;
          auth.mfa_enroll_advised = data.mfa_enroll_advised;
          auth.mfa_verify_required = data.mfa_verify_required;
          auth.mfa_last = data.mfa_last;
          auth.context_pending = false;
          auth.user = {
            id: data.user.id,
            email: data.user.email,
            username: data.user.username,
            language: data.user.language,
          };

          console.log('[AuthHTTP] mapped AuthModel:', auth);
          return auth;
        }),
        catchError((err: any) => {
          const api = err?.error;
          const message =
            api?.message ||
            api?.error ||
            (typeof api === 'string' ? api : null) ||
            err?.message ||
            'Login failed';
          return throwError(() => new Error(message));
        })
      );
  }

  /**
   * Refresh access token
   * Tries flexible shapes:
   * - POST /auth/refresh { refresh_token }
   * - POST /auth/refresh (Authorization: Bearer <accessToken>)
   * Returns: { success, data: { token, refresh_token?, expires_in? } }
   */
  refreshToken(currentAccessToken?: string, refreshToken?: string): Observable<{ token: string; refresh_token?: string; expires_in?: number } | undefined> {
    const url = `${authApiUrl()}/refresh`;
    const hasRefresh = typeof refreshToken === 'string' && refreshToken.length > 0;

    const headers = hasRefresh
      ? new HttpHeaders({ 'Content-Type': 'application/json' })
      : new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${currentAccessToken || ''}` });

    const body = hasRefresh ? { refresh_token: refreshToken } : {};

    return this.http.post<any>(url, body, { headers }).pipe(
      map((resp) => {
        const data = resp?.data ?? resp;
        const token = data?.token || data?.access_token;
        if (!token) {
          return undefined;
        }
        return {
          token,
          refresh_token: data?.refresh_token,
          expires_in: typeof data?.expires_in === 'number' ? data.expires_in : undefined,
        };
      }),
      catchError(() => of(undefined))
    );
  }

  /**
   * Get the currently selected customer for the authenticated contact.
   * Endpoint: GET /auth/contact/customer
   */
  getCurrentCustomer(token: string): Observable<CustomerModel | undefined> {
    if (!token) return of(undefined);
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const url = `${authApiUrl()}/contact/customer`;
    return this.http.get<any>(url, { headers }).pipe(
      map(resp => {
        const data = resp?.data ? resp.data : resp;
        // Supported shapes include:
        // { success, customer: {...} }
        // { success, tenant_id, customer_id, customer: {...} }
        // { success, data: { tenant_id, customer_id, customer } }
        const rawCustomer = data?.customer || data?.currentCustomer || undefined;
        if (!rawCustomer) return undefined;
        const customer: CustomerModel = {
          id: rawCustomer.id,
          is_business: rawCustomer.is_business,
          supplier_data: rawCustomer.supplier_data ?? null,
          commonname: rawCustomer.commonname ?? rawCustomer.name,
          address_id: rawCustomer.address_id ?? null,
          contact_id: rawCustomer.contact_id ?? 0,
          business_detail_id: rawCustomer.business_detail_id ?? null,
          status: rawCustomer.status,
          name: rawCustomer.name,
          email: rawCustomer.email ?? null,
          created_at: rawCustomer.created_at ?? '',
          updated_at: rawCustomer.updated_at ?? '',
          external_references: rawCustomer.external_references ?? null,
          is_tenant_owner: rawCustomer.is_tenant_owner ?? null,
          tenant_id: data?.tenant_id ?? rawCustomer.tenant_id ?? 0,
          tenant_name: rawCustomer.tenant_name ?? '',
          tags: rawCustomer.tags ?? (rawCustomer.type ? [rawCustomer.type] : [])
        };
        return customer;
      }),
      catchError(() => of(undefined))
    );
  }

  // Social login methods
  signInWithGoogle(): Observable<AuthModel | undefined> {
    console.log('[AuthHTTP] attempting Google sign-in');
    return this.http
      .post<any>(`${authApiUrl()}/google`, {})
      .pipe(
        tap(resp => console.log('[AuthHTTP] raw Google sign-in response', resp)),
        map(resp => {
          if (!resp?.success) {
            console.warn('[AuthHTTP] Google sign-in.success=false', resp);
            // Throw an error with the message from the API response
            throw new Error(resp.message || 'Google sign-in failed');
          }
          
          const data = resp.data;
          if (!data?.token || !data?.user) {
            console.error('[AuthHTTP] missing token or user in Google sign-in response', resp);
            throw new Error('Invalid response format: missing token or user data');
          }

          const auth = new AuthModel();
          auth.authToken = data.token;
          auth.refreshToken = '';
          auth.expiresIn = new Date(Date.now() + 24 * 60 * 60 * 1000);
          auth.userId = data.user.id;
          auth.customerId = data.customer_id || null;
          // Map the new fields from the API response
          auth.tenant_id = data.tenant_id;
          auth.customer_id = data.customer_id;
          auth.mfa_enroll_advised = data.mfa_enroll_advised;
          auth.mfa_verify_required = data.mfa_verify_required;
          auth.mfa_last = data.mfa_last;

          console.log('[AuthHTTP] mapped Google AuthModel:', auth);
          return auth;
        })
      );
  }

  signInWithApple(): Observable<AuthModel | undefined> {
    console.log('[AuthHTTP] attempting Apple sign-in');
    return this.http
      .post<any>(`${authApiUrl()}/apple`, {})
      .pipe(
        tap(resp => console.log('[AuthHTTP] raw Apple sign-in response', resp)),
        map(resp => {
          if (!resp?.success) {
            console.warn('[AuthHTTP] Apple sign-in.success=false', resp);
            // Throw an error with the message from the API response
            throw new Error(resp.message || 'Apple sign-in failed');
          }
          
          const data = resp.data;
          if (!data?.token || !data?.user) {
            console.error('[AuthHTTP] missing token or user in Apple sign-in response', resp);
            throw new Error('Invalid response format: missing token or user data');
          }

          const auth = new AuthModel();
          auth.authToken = data.token;
          auth.refreshToken = '';
          auth.expiresIn = new Date(Date.now() + 24 * 60 * 60 * 1000);
          auth.userId = data.user.id;
          auth.customerId = data.customer_id || null;
          // Map the new fields from the API response
          auth.tenant_id = data.tenant_id;
          auth.customer_id = data.customer_id;
          auth.mfa_enroll_advised = data.mfa_enroll_advised;
          auth.mfa_verify_required = data.mfa_verify_required;
          auth.mfa_last = data.mfa_last;

          console.log('[AuthHTTP] mapped Apple AuthModel:', auth);
          return auth;
        })
      );
  }


  // Two-factor authentication methods using EF-IT Portal API v2.0.0
  getTwoFactorSetup(): Observable<any> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return of(undefined);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.post<any>(`${environment.apiUrl}/auth/mfa/setup-totp`, {}, { headers });
  }

  generateTwoFactorSetup(): Observable<any> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return of(undefined);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.post<any>(`${environment.apiUrl}/auth/mfa/setup-totp`, {}, { headers });
  }

  verifyTwoFactor(code: string): Observable<AuthModel | undefined> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return of(undefined);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http
      .post<any>(`${environment.apiUrl}/auth/mfa/verify-totp`, { code }, { headers })
      .pipe(
        map(resp => {
          if (!resp?.success) {
            throw new Error(resp.message || 'Two-factor verification failed');
          }

          const data = resp?.data ?? resp;
          const tokenValue = data?.token;
          if (!tokenValue) {
            throw new Error('Invalid response format: missing token data');
          }

          const auth = new AuthModel();
          auth.authToken = tokenValue;
          auth.refreshToken = data.refresh_token || '';
          auth.expiresIn = new Date(Date.now() + 24 * 60 * 60 * 1000);
          auth.userId = data.user?.id ?? 0;
          auth.customerId = data.customer_id || null;
          auth.tenant_id = data.tenant_id;
          auth.customer_id = data.customer_id;
          if (data.user?.id) {
            auth.user = {
              id: data.user.id,
              email: data.user.email,
              username: data.user.username,
              language: data.user.language,
            };
          }

          return auth;
        })
      );
  }

  verifyTwoFactorSetup(code: string, authMethodId?: number): Observable<any> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return of(undefined);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const body: Record<string, unknown> = {
      code,
      setup_verification: true,
    };
    if (authMethodId) {
      body.auth_method_id = authMethodId;
    }
    return this.http.post<any>(`${environment.apiUrl}/auth/mfa/verify-totp`, body, { headers });
  }

  resendTwoFactorCode(): Observable<any> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return of(undefined);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.post<any>(`${environment.apiUrl}/auth/mfa/request`, {}, { headers });
  }

  regenerateBackupCodes(): Observable<any> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return of(undefined);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.post<any>(`${environment.apiUrl}/auth/mfa/backup-codes/regenerate`, {}, { headers });
  }

  // Email MFA methods
  setupEmailMfa(): Observable<any> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return of(undefined);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.post<any>(`${environment.apiUrl}/auth/mfa/setup-email`, {}, { headers });
  }

  requestEmailMfa(): Observable<any> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return of(undefined);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.post<any>(`${environment.apiUrl}/auth/mfa/request`, {}, { headers });
  }

  verifyEmailMfa(code: string): Observable<AuthModel | undefined> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return of(undefined);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http
      .post<any>(`${environment.apiUrl}/auth/mfa/verify-email`, { code }, { headers })
      .pipe(
        map(resp => {
          if (!resp?.success) {
            throw new Error(resp.message || resp.error || 'Email MFA verification failed');
          }

          // Accept both { data: { token } } (loginSuccess) and legacy { token }
          const data = resp?.data ?? resp;
          const tokenValue = data?.token;
          if (!tokenValue) {
            throw new Error('Invalid response format: missing token data');
          }

          const auth = new AuthModel();
          auth.authToken = tokenValue;
          auth.refreshToken = data.refresh_token || '';
          auth.expiresIn = new Date(Date.now() + 24 * 60 * 60 * 1000);
          auth.userId = data.user?.id ?? 0;
          auth.customerId = data.customer_id || null;
          auth.tenant_id = data.tenant_id;
          auth.customer_id = data.customer_id;
          if (data.user?.id) {
            auth.user = {
              id: data.user.id,
              email: data.user.email,
              username: data.user.username,
              language: data.user.language,
            };
          }
          return auth;
        })
      );
  }

  /**
   * Get allowed sign-in methods from the API
   */
  getAllowedAuthMethods(): Observable<string[]> {
    return this.http.get<any>(`${authApiUrl()}/methods`).pipe(
      map(resp => {
        let methods: unknown = [];
        if (resp?.methods) {
          methods = resp.methods;
        } else if (resp?.data?.methods) {
          methods = resp.data.methods;
        } else if (Array.isArray(resp?.data)) {
          methods = resp.data;
        } else if (Array.isArray(resp)) {
          methods = resp;
        }

        if (!Array.isArray(methods)) {
          return [];
        }

        return (methods as unknown[])
          .map(m => (m ?? '').toString())
          .filter(m => m.length > 0);
      })
    );
  }

  // … the rest of your methods stay unchanged …

  createUser(user: UserModel): Observable<UserModel> {
    return this.http.post<UserModel>(authApiUrl(), {
      email: user.email,
      password: user.password,
      fullname: user.fullname,
    });
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post<any>(`${authApiUrl()}/forgot-password`, { email });
  }

  validateResetToken(token: string): Observable<any> {
    return this.http.get<any>(`${authApiUrl()}/validate-reset-token`, {
      params: { token },
    });
  }

  resetPassword(token: string, password: string): Observable<any> {
    return this.http.post<any>(`${authApiUrl()}/reset-password`, { token, password });
  }

  getUserByToken(token: string): Observable<UserModel | undefined> {
    if (!token) {
      console.warn('[AuthHTTP] getUserByToken called without token');
      return of(undefined);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    console.log('[AuthHTTP] fetching user info with token…');

    return this.http.get<any>(`${authApiUrl()}/contact`, { headers }).pipe(
      tap(resp => console.log('[AuthHTTP] /contact response', resp)),
      map(resp => {
        // Handle both possible response structures
        const contactData: any = resp?.data ?? resp;

        if (!contactData?.id) {
          console.error('[AuthHTTP] getUserByToken: incomplete /contact response', resp);
          return undefined;
        }

        const user = new UserModel();
        user.id = contactData.id;
        user.email = contactData.email;
        // Prefer explicit first/last or fullname from API
        user.firstname = contactData.firstname || '';
        user.lastname = contactData.lastname || '';
        user.fullname = contactData.fullname || `${user.firstname} ${user.lastname}`.trim();
        user.username = contactData.username || contactData.email;
        user.language = contactData.language || 'en';

        // Contact details
        user.phone = contactData.telephone || contactData.phone || '';
        user.mobile = contactData.mobile || '';
        user.companyName = contactData.company?.commonname || '';
        // Prefer avatar_url; fallback to icon/avatar uuid if provided
        if (typeof contactData.avatar_url === 'string' && contactData.avatar_url.trim().length > 0) {
          user.pic = contactData.avatar_url;
        } else if (typeof contactData.icon_uuid === 'string' && contactData.icon_uuid.trim().length > 0) {
          user.pic = `${environment.apiUrl}/images/${contactData.icon_uuid}`;
        } else if (typeof contactData.avatar_uuid === 'string' && contactData.avatar_uuid.trim().length > 0) {
          user.pic = `${environment.apiUrl}/images/${contactData.avatar_uuid}`;
        } else {
          user.pic = './assets/media/avatars/blank.png';
        }

        // Optional extras
        user.timeZone = 'Europe/Amsterdam';
        user.communication = {
          email: true,
          sms: false,
          phone: false,
        };

        // MFA info from /auth/contact
        user.mfa_verify_required = Boolean(contactData.mfa_verify_required);
        user.mfa_last = contactData.mfa_last;
        // Map to mfa_status structure for compatibility
        const methods = Array.isArray(contactData.mfa_methods) ? contactData.mfa_methods : [];
        user.mfa_status = {
          enabled: Boolean(contactData.mfa_enabled),
          type: methods?.[0]?.type || '',
          methods
        };

        user.search_debounce_ms = contactData.search_debounce_ms ?? undefined;
        user.search_debounce_ms_default = contactData.search_debounce_ms_default ?? undefined;
        user.search_debounce_ms_override =
          contactData.search_debounce_ms_override === null ||
          contactData.search_debounce_ms_override === undefined
            ? null
            : Number(contactData.search_debounce_ms_override);

        this.searchDebounce.applyFromContact(contactData);

        console.debug('[AuthHTTP] mapped UserModel from /contact:', user);
        return user;
      })
    );
  }


  getUserCompanies(token: string, search?: string): Observable<{
    currentCustomer: CustomerModel | undefined;
    availableCustomers: CustomerModel[];
  }> {
    let url = `${authApiUrl()}/managed-customers`;
    if (search && search.trim().length > 0) {
      const params = new URLSearchParams({ search: search.trim() });
      url = `${url}?${params.toString()}`;
    }
    console.log(`[AuthHTTP] loading companies${search ? ` with search="${search}"` : ''}`);

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return this.http.get<any>(url, { headers }).pipe(
      tap(response => console.log('[AuthHTTP] raw managed-customers response:', response)),
      map(response => {
        const data = response?.data ?? response;

        // New structure: { managed_tenants: [ { tenant_id, tenant_name, customers: CustomerModel[], has_more_customers } ] }
        type ManagedTenant = { tenant_id: number; tenant_name: string; customers: any[]; has_more_customers?: boolean };
        const managedTenants: ManagedTenant[] = Array.isArray(data?.managed_tenants) ? data.managed_tenants : [];
        const flattenedCustomers: CustomerModel[] = managedTenants.reduce((acc: CustomerModel[], t: ManagedTenant) => {
          const customers = Array.isArray(t?.customers) ? t.customers : [];
          const mapped = customers.map((c: any) => ({
            ...c,
            tenant_id: (t && typeof t.tenant_id === 'number') ? t.tenant_id : c?.tenant_id,
            tenant_name: (t && typeof t.tenant_name === 'string') ? t.tenant_name : c?.tenant_name,
          }));
          acc.push(...mapped);
          return acc;
        }, []);

        // Keep currentCustomer undefined for now (API can extend later)
        const currentCustomer: CustomerModel | undefined = undefined;

        console.log('[AuthHTTP] flattened customers:', flattenedCustomers);
        return {
          availableCustomers: flattenedCustomers,
          currentCustomer
        };
      })
    );
  }

  /**
   * Get managed tenants with up to 20 customers per tenant, and has_more_customers flag
   * Optionally pass search, tenant_id and page to support future paging extensions
   */
  getManagedTenants(
    token: string,
    options?: { search?: string; tenant_id?: number; page?: number }
  ): Observable<ManagedTenantDto[]> {
    let url = `${authApiUrl()}/managed-customers`;
    const params: Record<string, string> = {};
    if (options?.search && options.search.trim().length > 0) params.search = options.search.trim();
    if (typeof options?.tenant_id === 'number') params.tenant_id = String(options.tenant_id);
    if (typeof options?.page === 'number') params.page = String(options.page);
    const qs = new URLSearchParams(params);
    if (Object.keys(params).length > 0) url = `${url}?${qs.toString()}`;

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return this.http.get<any>(url, { headers }).pipe(
      map((resp) => {
        const data = resp?.data ?? resp;
        const managedTenants: ManagedTenantDto[] = Array.isArray(data?.managed_tenants)
          ? data.managed_tenants.map((t: any) => ({
              tenant_id: t.tenant_id,
              tenant_name: t.tenant_name,
              total_customers: t.total_customers,
              customers: Array.isArray(t.customers) ? t.customers : [],
              has_more_customers: Boolean(t.has_more_customers)
            }))
          : [];
        return managedTenants;
      })
    );
  }

  /**
   * Get a page of customers for a specific tenant (lazy loading)
   * /auth/managed-customers?tenant_id=123&offset=0&limit=20&search=...
   */
  getTenantCustomersPage(
    token: string,
    tenantId: number,
    options?: { offset?: number; limit?: number; search?: string }
  ): Observable<TenantCustomersPageDto> {
    let url = `${authApiUrl()}/managed-customers`;
    const params: Record<string, string> = { tenant_id: String(tenantId) };
    if (typeof options?.offset === 'number') params.offset = String(options.offset);
    if (typeof options?.limit === 'number') params.limit = String(options.limit);
    if (options?.search && options.search.trim().length > 0) params.search = options.search.trim();
    const qs = new URLSearchParams(params);
    url = `${url}?${qs.toString()}`;

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.get<any>(url, { headers }).pipe(
      map((resp) => {
        const data = resp?.data ?? resp;
        const page: TenantCustomersPageDto = {
          total: Number(data?.total) || 0,
          customers: Array.isArray(data?.customers) ? data.customers : [],
          has_more: Boolean(data?.has_more)
        };
        return page;
      })
    );
  }

  getUserEntities(token: string): Observable<{
    currentEntity: EntityModel | undefined;
    availableEntities: EntityModel[];
  }> {
    const url = `${authApiUrl()}/managed-customers`;
    console.log(`[AuthHTTP] loading entities from managed-customers`);

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return this.http.get<any>(url, { headers }).pipe(
      tap(response => console.log('[AuthHTTP] raw managed-customers response for entities:', response)),
      map(response => {
        const data = response?.data ?? response;
        type ManagedTenant = { tenant_id: number; tenant_name: string; customers: any[]; has_more_customers?: boolean };
        const managedTenants: ManagedTenant[] = Array.isArray(data?.managed_tenants) ? data.managed_tenants : [];
        const flattenedCustomers: CustomerModel[] = managedTenants.reduce((acc: CustomerModel[], t: ManagedTenant) => {
          const customers = Array.isArray(t?.customers) ? t.customers : [];
          const mapped = customers.map((c: any) => ({
            ...c,
            tenant_id: (t && typeof t.tenant_id === 'number') ? t.tenant_id : c?.tenant_id,
            tenant_name: (t && typeof t.tenant_name === 'string') ? t.tenant_name : c?.tenant_name,
          }));
          acc.push(...mapped);
          return acc;
        }, []);

        const availableEntities = flattenedCustomers.map((customer: CustomerModel) => ({
          id: customer.id,
          contact_id: customer.contact_id,
          customer_id: customer.id,
          role: 'OWNER',
          status: customer.status,
          created_at: customer.created_at,
          updated_at: customer.updated_at,
          customer: {
            id: customer.id,
            name: customer.name,
            commonname: customer.commonname || customer.name,
            email: customer.email || '',
            tenant_id: customer.tenant_id,
            tenant_name: customer.tenant_name,
            status: customer.status
          },
          entity_name: customer.name,
          entity_commonname: customer.commonname || customer.name,
          entity_email: customer.email || '',
          tenant_name: customer.tenant_name,
          is_owner: true
        }));

        const currentEntity: EntityModel | undefined = undefined;

        return {
          availableEntities,
          currentEntity
        };
      })
    );
  }


  selectCustomer(customerId: number, token: string, tenantId?: number): Observable<{ token?: string, customer_id?: number } | undefined> {
    if (!customerId || !token) {
      console.warn('[AuthHTTP] selectCustomer called without customerId or token');
      return of(undefined);
    }

    const url = `${authApiUrl()}/select-customer`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    const body: any = {
      customer_id: customerId
    };
    if (typeof tenantId === 'number') {
      body.tenant_id = tenantId;
    }

    console.log(`[AuthHTTP] selecting customer with ID ${customerId}...`);

    return this.http.post<any>(url, body, { headers }).pipe(
      tap(resp => console.log('[AuthHTTP] /select-customer response:', resp)),
      map(resp => {
        // Handle both possible response structures
        let data: any;
        
        // Accept both styles: flat fields or nested under data
        data = resp?.data ? resp.data : resp;

        // Do not require token; some backends may only return customer_id
        if (data?.token) {
          localStorage.setItem('authToken', data.token);
        }
        return { token: data?.token, customer_id: data?.customer_id };
      })
    );
  }

  selectEntity(customerId: number, token: string, tenantId?: number): Observable<{ token?: string, customer_id?: number } | undefined> {
    // Use the same endpoint as selectCustomer but with entity terminology
    return this.selectCustomer(customerId, token, tenantId);
  }

  /**
   * Get authentication headers for API requests
   */
  private getAuthHeaders(token?: string): HttpHeaders {
    const authToken = token || localStorage.getItem('authToken');
    return new HttpHeaders({
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    });
  }

  // ===== PASSKEY AUTHENTICATION METHODS =====

  /**
   * Start passkey registration process
   * POST /auth/passkey/register/begin
   */
  startPasskeyRegistration(): Observable<any> {
    return this.http.post<any>(`${authApiUrl()}/passkey/register/begin`, {}, { headers: this.getAuthHeaders() })
      .pipe(
        map(response => {
          console.log('[AuthHTTP] Passkey registration begin response:', response);
          if (response.success) {
            // Return the entire response since challenge data is at root level
            return response;
          }
          return null;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Complete passkey registration
   * POST /auth/passkey/register/complete
   */
  completePasskeyRegistration(credential: any, name?: string): Observable<any> {
    console.log('[AuthHTTP] Sending credential to server for registration completion:', credential);
    console.log('[AuthHTTP] Credential structure:', JSON.stringify(credential, null, 2));
    console.log('[AuthHTTP] Passkey name:', name);
    
    // Prepare request body with credential wrapped in response field and optional name
    const requestBody: any = { 
      response: {
        id: credential.id,
        type: credential.type,
        rawId: credential.rawId,
        response: {
          clientDataJSON: credential.response.clientDataJSON,
          attestationObject: credential.response.attestationObject
        }
      }
    };
    if (name) {
      requestBody.name = name;
    }
    
    return this.http.post<any>(`${authApiUrl()}/passkey/register/complete`, requestBody, { headers: this.getAuthHeaders() })
      .pipe(
        map(response => {
          console.log('[AuthHTTP] Passkey registration completion response:', response);
          return response.success ? response.data : null;
        }),
        catchError(error => {
          console.error('[AuthHTTP] Passkey registration completion error:', error);
          console.error('[AuthHTTP] Error status:', error.status);
          console.error('[AuthHTTP] Error message:', error.message);
          console.error('[AuthHTTP] Error body:', error.error);
          console.error('[AuthHTTP] Full error object:', error);
          return this.handleError(error);
        })
      );
  }

  /**
   * Start passkey authentication process (for sign-in)
   * POST /auth/passkey/authenticate/begin
   */
  startPasskeyAuthentication(): Observable<any> {
    return this.http.post<any>(`${authApiUrl()}/passkey/authenticate/begin`, {})
      .pipe(
        map(response => {
          console.log('[AuthHTTP] Passkey authentication begin response:', response);
          console.log('[AuthHTTP] Response type:', typeof response);
          console.log('[AuthHTTP] Response keys:', response ? Object.keys(response) : 'null/undefined');
          
          if (!response || !response.success) {
            console.log('[AuthHTTP] Invalid response structure:', { response, success: response?.success });
            throw new Error('Passkey authentication not supported by server');
          }
          // Return the entire response since challenge data is at root level
          return response;
        }),
        catchError(error => {
          console.error('[AuthHTTP] Passkey authentication begin error:', error);
          console.error('[AuthHTTP] Error status:', error.status);
          console.error('[AuthHTTP] Error message:', error.message);
          if (error.status === 404) {
            throw new Error('Passkey authentication endpoints are not available on this server');
          }
          return this.handleError(error);
        })
      );
  }

  /**
   * Complete passkey authentication (for sign-in)
   * POST /auth/passkey/authenticate/complete
   */
  completePasskeyAuthentication(credential: any): Observable<any> {
    const requestBody = {
      credential: {
        id: credential.id,
        type: credential.type,
        rawId: credential.rawId,
        response: {
          clientDataJSON: credential.response.clientDataJSON,
          authenticatorData: credential.response.authenticatorData,
          signature: credential.response.signature,
          userHandle: credential.response.userHandle
        }
      }
    };
    return this.http.post<any>(`${authApiUrl()}/passkey/authenticate/complete`, requestBody)
      .pipe(
        map(response => {
          console.log('[AuthHTTP] Passkey authentication complete response:', response);
          if (!response || !response.success) {
            throw new Error('Passkey authentication failed');
          }
          // Normalize different possible backend shapes to { success, data: { token, user }, message }
          const token = (response && response.data && response.data.token) ?? response?.token;
          const user  = (response && response.data && response.data.user)  ?? response?.user;

          if (!token) {
            console.warn('[AuthHTTP] No token found in passkey response. Raw response:', response);
          }

          return {
            success: Boolean(response.success),
            data: { token, user },
            message: response?.message
          };
        }),
        catchError(error => {
          console.error('[AuthHTTP] Passkey authentication complete error:', error);
          if (error.status === 404) {
            throw new Error('Passkey authentication endpoints are not available on this server');
          }
          return this.handleError(error);
        })
      );
  }

  /**
   * Start passkey authentication process (for authenticated users)
   * POST /auth/passkey/authenticate/begin
   */
  startPasskeyAuthenticationAuthenticated(): Observable<any> {
    return this.http.post<any>(`${authApiUrl()}/passkey/authenticate/begin`, {}, { headers: this.getAuthHeaders() })
      .pipe(
        map(response => {
          console.log('[AuthHTTP] Passkey authentication begin (authenticated) response:', response);
          if (response.success) {
            // Return the entire response since challenge data is at root level
            return response;
          }
          return null;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Complete passkey authentication (for authenticated users)
   * POST /auth/passkey/authenticate/complete
   */
  completePasskeyAuthenticationAuthenticated(credential: any): Observable<any> {
    const requestBody = {
      credential: {
        id: credential.id,
        type: credential.type,
        rawId: credential.rawId,
        response: {
          clientDataJSON: credential.response.clientDataJSON,
          authenticatorData: credential.response.authenticatorData,
          signature: credential.response.signature,
          userHandle: credential.response.userHandle
        }
      }
    };
    return this.http.post<any>(`${authApiUrl()}/passkey/authenticate/complete`, requestBody, { headers: this.getAuthHeaders() })
      .pipe(
        map(response => response.success ? response.data : null),
        catchError(this.handleError)
      );
  }

  /**
   * List user's passkeys
   * GET /auth/passkey/list
   */
  listPasskeys(): Observable<any[]> {
    // No session → no list (anonymous /auth/passkey). Avoids a guaranteed 401 + toast cascade.
    if (!localStorage.getItem('authToken')) {
      return of([]);
    }
    return this.http.get<any>(`${authApiUrl()}/passkey/list`, { headers: this.getAuthHeaders() })
      .pipe(
        map(response => {
          // Backend returns passkeys in 'passkeys' key, not 'data'
          return response.success ? (response.passkeys || response.data || []) : [];
        }),
        catchError(error => {
          console.warn('[AuthHTTP] Passkey list error:', error?.status, error?.message);
          return this.handleError(error);
        })
      );
  }

  /**
   * Delete a passkey
   * DELETE /auth/passkey/delete
   */
  deletePasskey(passkeyId: string): Observable<boolean> {
    return this.http.delete<any>(`${authApiUrl()}/passkey/delete`, { 
      headers: this.getAuthHeaders(),
      body: { credential_id: passkeyId }
    })
      .pipe(
        map(response => response.success),
        catchError(this.handleError)
      );
  }

  /**
   * Rename a passkey
   * PUT /auth/passkey/rename
   */
  renamePasskey(passkeyId: string, newName: string): Observable<boolean> {
    console.log('[AuthHTTP] Renaming passkey:', { passkeyId, newName });
    return this.http.put<any>(`${authApiUrl()}/passkey/rename`, { 
      credential_id: passkeyId,
      name: newName 
    }, { headers: this.getAuthHeaders() })
      .pipe(
        map(response => {
          console.log('[AuthHTTP] Passkey rename response:', response);
          return response.success;
        }),
        catchError(error => {
          console.error('[AuthHTTP] Passkey rename error:', error);
          return this.handleError(error);
        })
      );
  }

  private handleError(error: any): Observable<never> {
    console.error('Auth HTTP service error:', error);
    if (error?.status === 0) {
      return throwError(() => new Error(
        'Cannot reach the API (network or certificate error). Use https://portaldev.ef-it.nl and ensure the API is reachable.'
      ));
    }
    const api = error?.error;
    const message =
      api?.message ||
      api?.error ||
      (typeof api === 'string' ? api : null) ||
      error?.message ||
      'Request failed';
    return throwError(() => new Error(message));
  }
}
